import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Target, Users, Heart, ArrowRight, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { teamMembers } from '../data/mockData';
import ReviewCarousel from '../components/ReviewCarousel';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import { aboutPageSchema } from '../lib/schema';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO 
        title="About Paint It Better | Kent Painters & Decorators"
        description="Family-run painting and decorating business in Kent. Checkatrade-approved, 10/10 reviews. Serving Wye, Ashford, Canterbury and surrounding villages."
        canonical="/about"
      />
      <JsonLd data={aboutPageSchema()} />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              About Paint It Better
            </h1>
            <p className="text-xl text-cyan-100">
              Your trusted painting and decorating specialists in Kent, dedicated to transforming homes with quality and care.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Caring for Your Home, Inside and Out
              </h2>
              <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                <p>
                  Paint It Better is a family-run painting and decorating business based in Kent, England. We take pride in providing friendly, professional, and down-to-earth services that transform houses into homes.
                </p>
                <p>
                  With over 15 years of experience, our skilled team of experts brings dedication and craftsmanship to every project. From interior painting to exterior renovations and flooring installation, we approach each job with the same level of care and attention to detail.
                </p>
                <p>
                  We believe that quality workmanship doesn't have to come with a premium price tag. That's why we work closely with our clients to understand their needs and budget, ensuring that everyone can achieve the home of their dreams.
                </p>
                <p className="bg-slate-50 p-4 rounded-lg border-l-4 border-slate-300">
                  Many of our clients are happy to leave us a key while they're at work or away. We lock up securely at the end of each day and send <strong>photo and video updates with commentary</strong>, so you can follow progress and see exactly what's been done without needing to be on site.
                </p>
                <p className="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-600">
                  <strong>Paint It Better is a{' '}
                  <a 
                    href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
                    target="_blank" 
                    rel="noopener"
                    className="text-cyan-600 hover:text-cyan-700 underline decoration-2 underline-offset-2 font-semibold"
                  >
                    Checkatrade-approved painter and decorator
                  </a>{' '}
                  in Kent.</strong> Checkatrade vets all its members to ensure they meet high standards of quality and professionalism. You can read our verified customer reviews and see examples of our work on our Checkatrade profile.
                </p>
              </div>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="/images/emergent/about-bedroom.jpg" 
                alt="Beautifully decorated bedroom in Kent featuring rich green feature wall with ornate mirror and cream wall paneling by Paint It Better"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Award,
                title: 'Quality First',
                description: 'We use only high-quality materials and proven techniques to ensure lasting results'
              },
              {
                icon: Target,
                title: 'Attention to Detail',
                description: 'Every corner, every surface receives our meticulous care and professional finish'
              },
              {
                icon: Users,
                title: 'Customer Focus',
                description: 'Your satisfaction is our priority. We listen, understand, and deliver on your vision'
              },
              {
                icon: Heart,
                title: 'Honest & Friendly',
                description: 'Down-to-earth service with transparent pricing and genuine care for your home'
              }
            ].map((value, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center group-hover:from-cyan-600 group-hover:to-teal-600 transition-all duration-300">
                    <value.icon className="h-10 w-10 text-cyan-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Why Choose Paint It Better?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Over 15 years of professional experience',
                '500+ successfully completed projects',
                'Fully insured and qualified tradespeople',
                'Free, no-obligation quotes',
                'Competitive and transparent pricing',
                'High-quality materials and finishes',
                'Respectful and tidy work practices',
                'Excellent customer service and aftercare',
                'Flexible scheduling to suit you',
                'Local business serving Kent communities'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3 group">
                  <CheckCircle2 className="h-6 w-6 text-cyan-600 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-slate-700 text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section with Checkatrade */}
      <section className="py-20 bg-gradient-to-b from-cyan-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
              Don't just take our word for it - see what our customers have to say about our work
            </p>
          </div>

          {/* Checkatrade Trust Box */}
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Verified Checkatrade Member
              </div>
              <p className="text-lg text-slate-700 max-w-2xl mx-auto">
                As a vetted member of <strong>Checkatrade</strong>, the UK's most trusted trade directory, 
                all our reviews are from genuine customers. Checkatrade verifies every review to ensure 
                you're reading honest feedback from real homeowners in Kent and the surrounding areas.
              </p>
              <a 
                href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
                target="_blank" 
                rel="noopener"
                className="inline-flex items-center justify-center bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Read our verified Checkatrade reviews
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <p className="text-sm text-slate-500">
                Over 100 reviews from satisfied customers across Kent
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-xl mb-8 text-cyan-100">
              Experience the Paint It Better difference. Contact us today for your free consultation.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-cyan-50 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl">
                Get Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Review Carousel */}
      <ReviewCarousel />
    </div>
  );
};

export default About;